import dayjs from 'dayjs';
import { varAlpha } from 'minimal-shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@classroom-ai/ui/routes/hooks';
import { MathMarkdown } from '@classroom-ai/ui/components/math';
import { RouterLink } from '@classroom-ai/ui/routes/components';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Iconify, type IconifyName } from '@classroom-ai/ui/components/iconify';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';

import { useActiveClass } from 'src/class/class-provider';
import { invalidateAll } from 'src/lib/invalidate-queries';
import { contextChip } from 'src/layouts/header/header-model';
import { agentTrpc, StreamUnavailableError } from 'src/lib/agent-trpc';

import { MARKDOWN_COMPONENTS } from 'src/components/markdown';

import { usePermissions } from 'src/auth/use-permissions';

import { shortcutHint } from './beacon-shortcut';
import { AskAiDoAll, AskAiActionCard } from './ask-ai-action-card';
import {
  batchable,
  EMPTY_LIVE,
  starterIntro,
  starterPrompts,
  loadTranscript,
  saveTranscript,
  type AskAiLive,
  requestMessages,
  applyStreamEvent,
  type AskAiAction,
  type AskAiMessage,
  type AskAiViewContext,
} from './ask-ai-utils';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  classId: string;
  /** e.g. "7th Grade Math · Period 2" */
  classLabel: string;
  view: AskAiViewContext;
  /** Asked once when it arrives (a new `key` asks again). */
  initialQuestion?: { text: string; key: number } | null;
  /** Bumped on every open (button or shortcut) — puts the cursor in the question box. */
  focusKey?: number;
};

/**
 * Ask AI (epic #391): a right-side chat about the active class. Each question goes to the
 * agent with the recent conversation and the page the teacher is on; the reply is Markdown
 * whose in-app links (lessons) navigate and close the drawer. The transcript is kept per
 * class in this browser.
 *
 * Streaming (#405): the question is POSTed to the agent's SSE route first, so the reply
 * bubble shows each tool as it runs ("Checking 5 lessons' readiness…") and the answer as
 * it is written; the `done` frame carries the canonical answer (identical to the
 * `classAssistant.ask` mutation's) which replaces the provisional text. An agent without
 * the route makes the drawer fall back to the mutation.
 */
export function AskAiDrawer({ open, onClose, classId, classLabel, view, initialQuestion, focusKey }: Props) {
  const [messages, setMessages] = useState<AskAiMessage[]>(() => loadTranscript(classId));
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  /** The reply in progress (null when idle). */
  const [live, setLive] = useState<AskAiLive | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** The highlighted suggestion (↑ / ↓ from the empty question box; Enter asks it). */
  const [sel, setSel] = useState(0);
  const { viewing, schoolYearOptions, activeSchoolYearId } = useActiveClass();
  const yearChip = contextChip(schoolYearOptions.find((o) => o.yearId === activeSchoolYearId) ?? null, schoolYearOptions);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canRunActions = has('lesson:manage') && has('lesson:generate_ai');

  // Swap transcripts when the active class changes, then persist every change. The reload
  // runs first so one class's transcript is never saved under another's key.
  const loadedFor = useRef(classId);
  useEffect(() => {
    if (loadedFor.current !== classId) {
      loadedFor.current = classId;
      setMessages(loadTranscript(classId));
    }
  }, [classId]);
  useEffect(() => {
    if (loadedFor.current === classId) saveTranscript(classId, messages);
  }, [classId, messages]);

  // The cursor goes straight to the question box whenever Beacon opens (epic #617) — after
  // the drawer's own focus trap has run, hence the short delay.
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [open, focusKey]);

  // Keep the newest message (or the growing reply) in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending, live, open]);

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || pending) return;
      const next: AskAiMessage[] = [...messages, { role: 'user', content: q }];
      setMessages(next);
      setInput('');
      setPending(true);
      setLive(EMPTY_LIVE);
      const askedFor = classId;
      const stillHere = () => loadedFor.current === askedFor;
      const request = {
        classId,
        messages: requestMessages(next),
        viewContext: view.route === 'page' ? undefined : view,
        today: dayjs().format('YYYY-MM-DD'),
      };
      const reply = (answer: string, actions?: AskAiAction[]) =>
        setMessages((m) => [...m, { role: 'assistant', content: answer, ...(actions?.length ? { actions } : {}) }]);
      const fail = (msg: string) => setMessages((m) => [...m, { role: 'assistant', content: msg, error: true }]);
      try {
        let finished = false;
        try {
          await agentTrpc.classAssistant.askStream(request, (event) => {
            if (!stillHere()) return;
            if (event.type === 'done') {
              finished = true;
              reply(event.answer, event.actions);
            } else if (event.type === 'error') {
              finished = true;
              fail(event.message);
            } else {
              setLive((l) => applyStreamEvent(l ?? EMPTY_LIVE, event));
            }
          });
          if (!finished && stillHere()) fail('The connection dropped before the answer finished. Please ask again.');
        } catch (e) {
          if (!(e instanceof StreamUnavailableError)) throw e;
          // Older agent without the stream route: one plain request instead.
          const res = await agentTrpc.classAssistant.ask(request);
          if (stillHere()) reply(res.answer, res.actions);
        }
      } catch (e) {
        if (stillHere()) fail(e instanceof Error ? e.message : 'Something went wrong.');
      } finally {
        setLive(null);
        setPending(false);
      }
    },
    [classId, messages, pending, view]
  );

  // A question handed in by another surface (e.g. "Ask AI to explain") is asked once.
  const askedKey = useRef<number | null>(null);
  useEffect(() => {
    if (!open || !initialQuestion || askedKey.current === initialQuestion.key || pending) return;
    askedKey.current = initialQuestion.key;
    void ask(initialQuestion.text);
  }, [open, initialQuestion, pending, ask]);

  // Patch one card in the transcript (persisted with it).
  const patchAction = useCallback((actionId: string, patch: Partial<AskAiAction>) => {
    setMessages((ms) =>
      ms.map((m) =>
        m.actions?.some((a) => a.actionId === actionId)
          ? { ...m, actions: m.actions.map((a) => (a.actionId === actionId ? { ...a, ...patch } : a)) }
          : m
      )
    );
  }, []);

  // ── Background jobs (Phase G): approve → job queued → poll getJob → patch the cards. ──
  const polling = useRef(new Set<string>());
  const watchJob = useCallback(
    (jobId: string) => {
      if (polling.current.has(jobId)) return;
      polling.current.add(jobId);
      let misses = 0;
      const tick = async () => {
        try {
          const job = await agentTrpc.classAssistant.getJob({ jobId });
          misses = 0;
          for (const item of job.items) {
            const busy = item.status === 'queued' || item.status === 'running';
            patchAction(item.actionId, {
              running: busy,
              ...(item.result ? { outcome: item.result, ...(item.result.status === 'exists' ? { existing: true } : {}) } : {}),
              ...(item.error ? { failed: item.error } : {}),
            });
          }
          if (job.status === 'done' || job.status === 'failed') {
            polling.current.delete(jobId);
            if (job.items.some((i) => i.result?.status === 'created')) await invalidateAll(queryClient); // open editors refetch
            return;
          }
        } catch {
          if (++misses >= 5) {
            polling.current.delete(jobId); // agent unreachable — resumes on next open
            return;
          }
        }
        setTimeout(tick, 3000);
      };
      tick();
    },
    [patchAction, queryClient]
  );

  // Resume watching jobs that were still running when the page was left.
  useEffect(() => {
    for (const m of messages) for (const a of m.actions ?? []) if (a.running && a.jobId) watchJob(a.jobId);
  }, [messages, watchJob]);

  const startJob = useCallback(
    async (actions: AskAiAction[], start: () => Promise<{ jobId: string }>) => {
      for (const a of actions) patchAction(a.actionId, { failed: undefined, running: true });
      try {
        const { jobId } = await start();
        for (const a of actions) patchAction(a.actionId, { jobId });
        watchJob(jobId);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'That did not work.';
        for (const a of actions) patchAction(a.actionId, { running: false, failed: msg });
      }
    },
    [patchAction, watchJob]
  );

  const runAction = useCallback(
    (action: AskAiAction, replace: boolean) =>
      startJob([action], () => agentTrpc.classAssistant.executeAction({ actionId: action.actionId, replace })),
    [startJob]
  );

  const runAll = useCallback(
    (actions: AskAiAction[]) =>
      startJob(actions, () => agentTrpc.classAssistant.executeBatch({ actionIds: actions.map((a) => a.actionId) })),
    [startJob]
  );

  const revert = useCallback(
    async (action: AskAiAction) => {
      try {
        const { message } = await agentTrpc.classAssistant.revertAction({ actionId: action.actionId });
        patchAction(action.actionId, { revertedMessage: message, failed: undefined });
        await invalidateAll(queryClient);
      } catch (e) {
        patchAction(action.actionId, { failed: e instanceof Error ? e.message : 'Revert failed.' });
      }
    },
    [patchAction, queryClient]
  );

  const openEditor = useCallback(
    (href: string) => {
      onClose();
      router.push(href);
    },
    [onClose, router]
  );

  // In-app links (the agent cites lessons as /dashboard/…) route client-side and close the
  // drawer so the teacher lands on the page; anything else opens in a new tab.
  const components = {
    ...MARKDOWN_COMPONENTS,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
      href?.startsWith('/') ? (
        <Link component={RouterLink} href={href} onClick={onClose} underline="hover">
          {children}
        </Link>
      ) : (
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
          {children}
        </Link>
      ),
  };

  const prompts = starterPrompts(view);
  const suggesting = messages.length === 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: 1, sm: 440 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 1.25,
            display: 'grid',
            placeItems: 'center',
            color: 'common.white',
            bgcolor: 'secondary.main',
          }}
        >
          <Iconify icon="solar:stars-bold-duotone" width={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">Beacon</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {classLabel}
          </Typography>
        </Box>
        <Tooltip title="Clear conversation">
          <span>
            <IconButton
              size="small"
              aria-label="Clear conversation"
              disabled={pending || messages.length === 0}
              onClick={() => setMessages([])}
            >
              <Iconify icon="solar:trash-bin-trash-bold" width={18} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="Close Beacon">
          <Iconify icon="mingcute:close-line" width={20} />
        </IconButton>
      </Box>

      {viewing ? (
        <ScopeNote tone="info" icon="solar:eye-bold">
          View only. Beacon can answer questions about this class, but plans and fixes need {viewing.teacherName}.
        </ScopeNote>
      ) : yearChip?.tone === 'warning' ? (
        <ScopeNote tone="warning" icon="solar:clock-circle-bold">
          {yearChip.tooltip.replace("You're looking at", 'Answers come from')}
        </ScopeNote>
      ) : null}

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {messages.length === 0 ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {starterIntro(view)}{' '}
              {view.scope === 'course'
                ? 'Answers come from live data across the sections.'
                : `Answers come from ${view.othersClass || viewing ? 'this' : 'your'} class\u2019s live data.`}
            </Typography>
            <Box>
              <Typography variant="overline" color="text.disabled">
                Suggested
              </Typography>
              <Stack role="listbox" aria-label="Suggested questions" spacing={0.5} sx={{ mt: 0.5 }}>
                {prompts.map((p, i) => (
                  <ButtonBase
                    key={p}
                    role="option"
                    aria-selected={i === sel && !input}
                    disabled={pending}
                    onClick={() => ask(p)}
                    onMouseEnter={() => setSel(i)}
                    sx={(theme) => ({
                      justifyContent: 'flex-start',
                      gap: 1.25,
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.25,
                      textAlign: 'left',
                      typography: 'body2',
                      fontWeight: 600,
                      ...(i === sel && !input && { bgcolor: varAlpha(theme.vars.palette.secondary.mainChannel, 0.12) }),
                    })}
                  >
                    <Iconify icon="solar:stars-bold-duotone" width={16} sx={{ color: 'secondary.main', flexShrink: 0 }} />
                    <Box component="span" sx={{ flex: 1 }}>
                      {p}
                    </Box>
                    {i === sel && !input && <Kbd>↵</Kbd>}
                  </ButtonBase>
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((m, i) =>
              m.role === 'user' ? (
                <Box
                  key={i}
                  sx={{ alignSelf: 'flex-end', maxWidth: '85%', px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'primary.lighter' }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Typography>
                </Box>
              ) : (
                <Box
                  key={i}
                  sx={{
                    alignSelf: 'flex-start',
                    maxWidth: '100%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: m.error ? 'error.lighter' : 'background.neutral',
                    color: m.error ? 'error.darker' : undefined,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {m.error ? (
                    <Typography variant="body2">{m.content}</Typography>
                  ) : (
                    <MathMarkdown components={components}>{m.content}</MathMarkdown>
                  )}
                  {m.actions?.length ? (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      {canRunActions && batchable(m.actions).length >= 2 && (
                        <AskAiDoAll actions={batchable(m.actions)} onRun={runAll} />
                      )}
                      {m.actions.map((a) => (
                        <AskAiActionCard
                          key={a.actionId}
                          action={a}
                          canRun={canRunActions}
                          onRun={(replace) => runAction(a, replace)}
                          onRevert={() => revert(a)}
                          onOpenEditor={openEditor}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              )
            )}
            {pending && <LiveReply live={live ?? EMPTY_LIVE} components={components} />}
          </Stack>
        )}
      </Box>

      <Box sx={{ p: 2, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder={viewing ? `Ask about ${viewing.teacherName}'s class…` : 'Ask about this class…'}
            value={input}
            disabled={pending}
            inputRef={inputRef}
            onChange={(e) => {
              setInput(e.target.value);
              if (!e.target.value) setSel(0);
            }}
            onKeyDown={(e) => {
              const picking = suggesting && !input;
              if (picking && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                setSel((i) => (e.key === 'ArrowDown' ? Math.min(i + 1, prompts.length - 1) : Math.max(i - 1, 0)));
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ask(picking ? prompts[sel] ?? '' : input);
              }
            }}
            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' } }}
          />
          <LoadingButton variant="contained" color="secondary" loading={pending} disabled={!input.trim()} onClick={() => ask(input)}>
            Ask
          </LoadingButton>
        </Box>
        <Typography
          variant="caption"
          color="text.disabled"
          component="div"
          sx={{ mt: 1, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
        >
          <Kbd>/</Kbd> or <Kbd>{shortcutHint()}</Kbd> on any page{suggesting ? <>{' · '}<Kbd>↑</Kbd><Kbd>↓</Kbd> pick a suggestion</> : null}
          {' · '}<Kbd>Esc</Kbd> to close
        </Typography>
      </Box>
    </Drawer>
  );
}

// ----------------------------------------------------------------------

/**
 * The reply being worked on: one line per tool call (a spinner while it runs, a tick once
 * the next step began), a "Thinking…" line while the model composes between tools, and
 * the provisional answer text as it streams in. Replaced by the final message when `done`
 * arrives.
 */
function LiveReply({ live, components }: { live: AskAiLive; components: Record<string, unknown> }) {
  return (
    <Box
      data-testid="ask-ai-live"
      sx={{ alignSelf: 'flex-start', maxWidth: '100%', px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'background.neutral', overflowWrap: 'anywhere' }}
    >
      {live.steps.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: live.text || live.thinking ? 1 : 0 }}>
          {live.steps.map((step, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              {step.done ? (
                <Iconify icon="solar:check-circle-bold" width={14} sx={{ color: 'success.main', flexShrink: 0 }} />
              ) : (
                <CircularProgress size={12} sx={{ flexShrink: 0 }} />
              )}
              <Typography variant="caption">{step.label}</Typography>
            </Box>
          ))}
        </Stack>
      )}
      {live.text ? (
        <MathMarkdown components={components}>{live.text}</MathMarkdown>
      ) : live.thinking ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CircularProgress size={12} />
          <Typography variant="caption">{live.steps.length === 0 ? 'Thinking… this can take up to a minute.' : 'Thinking…'}</Typography>
        </Box>
      ) : null}
    </Box>
  );
}

// ----------------------------------------------------------------------

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        px: 0.6,
        minWidth: 20,
        textAlign: 'center',
        borderRadius: 0.75,
        border: 1,
        borderBottomWidth: 2,
        borderColor: 'divider',
        typography: 'caption',
        fontFamily: 'inherit',
        fontWeight: 600,
        lineHeight: 1.5,
        color: 'text.secondary',
        bgcolor: 'background.paper',
      }}
    >
      {children}
    </Box>
  );
}

/** One line under the drawer header saying what Beacon's answers cover (epic #617). */
function ScopeNote({ tone, icon, children }: { tone: 'info' | 'warning'; icon: IconifyName; children: React.ReactNode }) {
  return (
    <Box
      role="note"
      sx={(theme) => ({
        mx: 2.5,
        mt: 1.5,
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
        typography: 'caption',
        fontWeight: 600,
        color: `${tone}.darker`,
        bgcolor: `${tone}.lighter`,
        ...theme.applyStyles('dark', { color: `${tone}.lighter`, bgcolor: varAlpha(theme.vars.palette[tone].mainChannel, 0.16) }),
      })}
    >
      <Iconify icon={icon} width={16} sx={{ flexShrink: 0, mt: '1px' }} />
      <span>{children}</span>
    </Box>
  );
}
